<template>
    <div class="cost-analysis-system">
        <!-- 筛选与时间选择区 -->
        <el-card class="filter-section">
            <el-form>
                <el-row :gutter="20">
                    <el-col :span="5">
                        <el-form-item label="功能间选择">
                            <el-select v-model="selectedArea" placeholder="请选择功能间" @change="handleAreaChange">
                                <el-option label="全部功能间" value="all"></el-option>
                                <!-- 动态渲染功能间选项 -->
                                <el-option v-for="area in dynamicFunctionalAreas" :key="area.id" :label="area.name"
                                    :value="area.id">
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>

                    <el-col :span="5">
                        <el-form-item label="开始时间">
                            <el-date-picker v-model="startDate" type="date" placeholder="选择日期"></el-date-picker>
                        </el-form-item>
                    </el-col>
                    <el-col :span="5">
                        <el-form-item label="结束时间">
                            <el-date-picker v-model="endDate" type="date" placeholder="选择日期"></el-date-picker>
                        </el-form-item>
                    </el-col>
                    <el-col :span="5">
                        <el-form-item label="图表类型">
                            <el-select v-model="chartType" @change="initCostTrendChart">
                                <el-option label="折线图" value="line"></el-option>
                                <el-option label="饼图" value="pie"></el-option>
                                <el-option label="柱状图" value="bar"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-button @click="queryData" style="margin-left: 10px;" icon="el-icon-search"
                        type="primary">查询</el-button>
                    <el-button @click="resetFilters" style="margin-left: 10px;" icon="el-icon-refresh">重置</el-button>
                </el-row>
            </el-form>
        </el-card>

        <!-- 图表分析区 -->
        <el-col :span="24">
            <el-row :gutter="20">
                <!-- 按分页数据渲染图表 -->
                <el-col :span="8" v-for="area in paginatedFunctionalAreas" :key="area.areaId">
                    <el-card class="chart-card">
                        <div slot="header">
                            <span>{{ area.areaName }} - {{
                                chartType === 'line' ? '成本趋势' :
                                    chartType === 'bar' ? '成本对比' : '成本占比'
                            }}</span>
                        </div>
                        <div class="chart-container">
                            <canvas v-if="chartType !== 'pie'" :id="`canvas-${area.areaId}`"></canvas>
                            <div v-else :id="`echarts-${area.areaId}`" class="echarts-container"></div>
                        </div>
                    </el-card>
                </el-col>
            </el-row>
            <!-- 无数据提示 -->
            <div v-if="paginatedFunctionalAreas.length === 0 && filteredFunctionalAreas.length > 0" class="no-data">
                <el-empty description="当前页暂无数据"></el-empty>
            </div>
            <div v-if="filteredFunctionalAreas.length === 0 && realCostData.length > 0" class="no-data">
                <el-empty description="筛选后无数据"></el-empty>
            </div>
            <div v-if="realCostData.length === 0" class="no-data">
                <el-empty description="暂无成本数据"></el-empty>
            </div>
        </el-col>
        <!-- 分页控件区 -->
        <div class="pagination-section" v-if="filteredFunctionalAreas.length > pageSize">
            <el-pagination align="center" @size-change="handleSizeChange" @current-change="handleCurrentChange"
                :current-page="currentPage" :page-sizes="[3]" :page-size="pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="filteredFunctionalAreas.length"></el-pagination>
        </div>
    </div>
</template>

<script>
import { getFeeList } from "@/api/analysis";
import { Chart, registerables } from 'chart.js';
import * as echarts from 'echarts';
Chart.register(...registerables);

export default {
    data() {
        return {
            selectedArea: 'all',
            startDate: '',
            endDate: '',
            chartType: 'line',
            selectedCostTypes: ['labor', 'water', 'electricity', 'gas'],
            charts: {},
            realCostData: [], // 存储后端获取的真实数据
            dynamicFunctionalAreas: [], // 存储动态获取的功能间列表
            currentPage: 1, // 当前页码
            pageSize: 3,    // 每页显示数量
        };
    },

    computed: {
        // 筛选需要显示的功能间（排除"全部功能间"选项）
        filteredFunctionalAreas() {
            const allAreas = this.realCostData.filter(area => area.areaId !== 'all');
            if (this.selectedArea !== 'all') {
                return allAreas.filter(area => area.areaId === this.selectedArea);
            }
            return allAreas;
        },
        // 分页处理后的功能间数据
        paginatedFunctionalAreas() {
            const startIndex = (this.currentPage - 1) * this.pageSize;
            const endIndex = startIndex + this.pageSize;
            return this.filteredFunctionalAreas.slice(startIndex, endIndex);
        }
    },

    mounted() {
        window.addEventListener('resize', this.handleResize);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        // 销毁所有图表实例
        Object.values(this.charts).forEach(chart => {
            if (chart && chart.destroy) {
                chart.destroy();
            }
        });
    },

    created() {
        this.getList();
    },

    methods: {
        // 获取后端数据并格式化
        getList() {
            getFeeList({ userId: localStorage.getItem('userId') }).then(response => {
                // 格式化数据，并同时更新动态功能间列表
                this.formatBackendData(response.data);
                this.initCostTrendChart(); // 数据获取后初始化图表
            }).catch(error => {
                console.error("获取成本数据失败:", error);
                this.realCostData = [];
                this.dynamicFunctionalAreas = [];
            });
        },

        // 格式化后端数据为前端所需结构，并更新动态功能间列表
        formatBackendData(rawDataArray) {
            console.log("原始数据数组:", rawDataArray);
            if (!Array.isArray(rawDataArray) || rawDataArray.length === 0) {
                this.realCostData = [];
                this.dynamicFunctionalAreas = [];
                return;
            }

            // 提取公共时间轴（从第一个功能间中获取）
            const dateTime = rawDataArray[0]?.dateTime || [];
            const electricityFees = rawDataArray[0]?.electricityFees || [];
            const gasFees = rawDataArray[0]?.gasFees || [];

            const formattedAreas = [];
            const functionalAreasList = []; // 临时存储功能间列表

            // 处理各个功能间数据
            rawDataArray.forEach(areaItem => {
                const areaId = this.getAreaIdByAreaName(areaItem.name);

                // 添加到功能间列表
                functionalAreasList.push({
                    id: areaId,
                    name: areaItem.name
                });

                const details = dateTime.map((date, index) => ({
                    date,
                    labor: 0, // 后端无人工数据时设为0
                    water: areaItem.waterFees ? areaItem.waterFees[index] || 0 : 0,
                    electricity: electricityFees[index] || 0,
                    gas: gasFees[index] || 0
                }));

                formattedAreas.push({
                    areaId,
                    areaName: areaItem.name,
                    details
                });
            });

            // 处理"全部功能间"汇总数据
            const allAreaDetails = dateTime.map((date, index) => {
                const totalWater = rawDataArray.reduce((sum, item) => {
                    return sum + (item.waterFees ? item.waterFees[index] || 0 : 0);
                }, 0);
                return {
                    date,
                    labor: 0,
                    water: totalWater,
                    electricity: electricityFees[index] || 0,
                    gas: gasFees[index] || 0
                };
            });

            formattedAreas.unshift({
                areaId: 'all',
                areaName: '全部功能间',
                details: allAreaDetails
            });

            // 更新响应式数据
            this.realCostData = formattedAreas;
            this.dynamicFunctionalAreas = functionalAreasList;
        },

        // 根据功能间名称映射areaId
        getAreaIdByAreaName(name) {
            const nameMap = {
                '净菜间': 'mainKitchen',
                '冷冻间': 'mainKitchen1',
                '粗加工间': 'mainKitchen2',
                '烹饪间': 'mainKitchen3',
                '肉食间': 'meatKitchen',
                '面食间': 'noodleKitchen'
                // 可以在这里添加更多固定的映射，如果后端返回了新的名称，
                // 而你又希望它有一个特定的ID，可以在这里配置。
                // 如果没有匹配的，就返回一个基于名称的唯一ID，例如 name本身。
            };
            return nameMap[name] || name; // 如果没有映射，直接使用名称作为ID
        },

        // 初始化所有图表
        initCostTrendChart() {
            // 清除原有图表
            Object.values(this.charts).forEach(chart => {
                if (chart && chart.destroy) {
                    chart.destroy();
                }
            });
            this.charts = {};

            if (this.paginatedFunctionalAreas.length === 0) {
                return;
            }

            this.$nextTick(() => {
                this.paginatedFunctionalAreas.forEach(area => {
                    this.createAreaChart(area);
                });
            });
        },

        // 为每个功能间创建独立图表
        createAreaChart(area) {
            this.$nextTick(() => {
                let chartDom;
                if (this.chartType === 'pie') {
                    chartDom = document.getElementById(`echarts-${area.areaId}`);
                } else {
                    chartDom = document.getElementById(`canvas-${area.areaId}`);
                }

                if (!chartDom) {
                    console.error(`图表容器未找到: ${area.areaName}`);
                    return;
                }

                // 获取当前功能间的原始数据
                const areaData = this.realCostData.find(item => item.areaId === area.areaId);
                if (!areaData || !areaData.details || areaData.details.length === 0) {
                    console.warn(`功能间 "${area.areaName}" 没有可用数据。`);
                    return;
                }

                // 按时间范围筛选数据
                const startDateObj = this.startDate ? new Date(this.startDate) : null;
                const endDateObj = this.endDate ? new Date(this.endDate) : null;
                const filteredDetails = areaData.details.filter(detail => {
                    const detailDate = new Date(detail.date);
                    return (!startDateObj || detailDate >= startDateObj) && (!endDateObj || detailDate <= endDateObj);
                });

                if (filteredDetails.length === 0) {
                    console.warn(`在选定的时间范围内，功能间 "${area.areaName}" 没有数据。`);
                    return;
                }

                // 饼图使用ECharts实现
                if (this.chartType === 'pie') {
                    // 计算各成本类型总和
                    const sumLabor = filteredDetails.reduce((sum, item) => sum + item.labor, 0);
                    const sumWater = filteredDetails.reduce((sum, item) => sum + item.water, 0);
                    const sumElectricity = filteredDetails.reduce((sum, item) => sum + item.electricity, 0);
                    const sumGas = filteredDetails.reduce((sum, item) => sum + item.gas, 0);

                    // 初始化ECharts实例
                    if (this.charts[`echarts-${area.areaId}`]) {
                        this.charts[`echarts-${area.areaId}`].destroy();
                    }
                    const myChart = echarts.init(chartDom);
                    this.charts[`echarts-${area.areaId}`] = myChart;

                    // ECharts饼图配置
                    const option = {
                        legend: { top: 'bottom', textStyle: { fontSize: 12 } },
                        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
                        dataset: {
                            source: [
                                ['product', '成本金额'],
                                ['人工', sumLabor],
                                ['水费', sumWater],
                                ['电费', sumElectricity],
                                ['燃气', sumGas]
                            ]
                        },
                        series: [{
                            type: 'pie',
                            radius: ['0%', '60%'],
                            center: ['50%', '45%'],
                            label: { formatter: '{b}: {c} ({d}%)', textStyle: { fontSize: 11 } },
                            itemStyle: { borderColor: '#fff', borderWidth: 2 },
                            color: ['#4299e1', '#48bb78', '#f6ad55', '#f56565']
                        }]
                    };

                    myChart.setOption(option);
                    // 监听窗口大小变化
                    const resizeHandler = () => myChart.resize();
                    window.addEventListener('resize', resizeHandler);
                    this.charts[`echarts-${area.areaId}_resize`] = resizeHandler;
                } else {
                    // 折线图/柱状图使用Chart.js
                    const ctx = chartDom.getContext('2d');
                    if (!ctx) {
                        console.error(`无法获取Canvas上下文: ${area.areaName}`);
                        return;
                    }

                    const timeLabels = filteredDetails.map(detail => detail.date);
                    const datasets = this.getChartDatasets(filteredDetails);

                    // 如果已有图表实例，先销毁
                    if (this.charts[`canvas-${area.areaId}`]) {
                        this.charts[`canvas-${area.areaId}`].destroy();
                    }

                    this.charts[`canvas-${area.areaId}`] = new Chart(ctx, {
                        type: this.chartType,
                        data: { labels: timeLabels, datasets },
                        options: this.getChartOptions(area.areaName)
                    });
                }
            });
        },

        // 获取图表数据集（折线图/柱状图）
        getChartDatasets(details) {
            const datasets = [];
            // 人工成本
            if (this.selectedCostTypes.includes('labor')) {
                datasets.push({
                    label: '人工',
                    data: details.map(d => d.labor),
                    borderColor: '#4299e1',
                    ...(this.chartType === 'line' ? {
                        borderWidth: 3, fill: true, backgroundColor: 'rgba(66, 153, 225, 0.1)',
                        pointRadius: 0 // 新增：隐藏折线图数据点
                    } : { backgroundColor: '#4299e1', borderRadius: 4 })
                });
            }
            // 水费
            if (this.selectedCostTypes.includes('water')) {
                datasets.push({
                    label: '水费',
                    data: details.map(d => d.water),
                    borderColor: '#48bb78',
                    ...(this.chartType === 'line' ? {
                        borderWidth: 3, fill: true, backgroundColor: 'rgba(72, 187, 120, 0.1)', pointRadius: 0 // 新增：隐藏折线图数据点
                    } : { backgroundColor: '#48bb78', borderRadius: 4 })
                });
            }
            // 电费
            if (this.selectedCostTypes.includes('electricity')) {
                datasets.push({
                    label: '电费',
                    data: details.map(d => d.electricity),
                    borderColor: '#f6ad55',
                    ...(this.chartType === 'line' ? {
                        borderWidth: 3, fill: true, backgroundColor: 'rgba(246, 173, 85, 0.1)', pointRadius: 0 // 新增：隐藏折线图数据点
                    } : { backgroundColor: '#f6ad55', borderRadius: 4 })
                });
            }
            // 燃气费
            if (this.selectedCostTypes.includes('gas')) {
                datasets.push({
                    label: '燃气',
                    data: details.map(d => d.gas),
                    borderColor: '#f56565',
                    ...(this.chartType === 'line' ? {
                        borderWidth: 3, fill: true, backgroundColor: 'rgba(245, 101, 101, 0.1)', pointRadius: 0 // 新增：隐藏折线图数据点
                    } : { backgroundColor: '#f56565', borderRadius: 4 })
                });
            }
            return datasets;
        },

        // 获取图表配置项（折线图/柱状图）
        getChartOptions(areaName) {
            return {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { position: 'top', labels: { boxWidth: 12, usePointStyle: true } },
                    tooltip: {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        titleColor: '#333',
                        bodyColor: '#666',
                        borderColor: '#eee',
                        borderWidth: 1,
                        padding: 10,
                        boxPadding: 5,
                        usePointStyle: true
                    },
                    title: {
                        display: true,
                        text: `${areaName}成本数据`,
                        font: { size: 16, weight: 'bold' },
                        padding: { bottom: 15 }
                    }
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: { autoSkip: true, maxRotation: 45, minRotation: 45 },
                        title: { display: true, text: '日期', padding: { top: 10 } }
                    },
                    y: {
                        grid: { color: 'rgba(0, 0, 0, 0.05)' },
                        beginAtZero: true,
                        ticks: {
                            callback: function (value) {
                                return '¥' + value;
                            }
                        },
                        title: { display: true, text: '金额', padding: { right: 10 } }
                    }
                },
                animation: { duration: 1000, easing: 'easeOutQuart' }
            };
        },

        // 窗口大小变化时重绘图表
        handleResize() {
            Object.values(this.charts).forEach(chart => {
                if (chart && typeof chart.resize === 'function') {
                    chart.resize();
                }
            });
        },

        // 功能间变化时重新绘制图表
        handleAreaChange() {
            this.currentPage = 1;
            this.initCostTrendChart();
        },

        // 查询数据
        queryData() {
            this.currentPage = 1;
            this.initCostTrendChart();
        },

        // 重置筛选条件
        resetFilters() {
            this.selectedArea = 'all';
            this.startDate = '';
            this.endDate = '';
            this.chartType = 'line';
            this.currentPage = 1;
            this.initCostTrendChart();
        },

        // 分页大小变化
        handleSizeChange(val) {
            this.pageSize = val;
            this.currentPage = 1;
            this.initCostTrendChart();
        },

        // 当前页码变化
        handleCurrentChange(val) {
            this.currentPage = val;
            this.initCostTrendChart();
        }
    }
};
</script>

<style scoped>
/* ... (样式部分保持不变) ... */
.cost-analysis-system {
    padding: 20px;
    background-color: #f5f7fa;
    min-height: 100vh;
}

.filter-section {
    margin-bottom: 20px;
    padding: 15px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.pagination-section {
    padding: 20px 0;
    margin-top: auto;
    background-color: #fff;
    z-index: 2;
    position: fixed;
    width: 100%;
    bottom: 0;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

.chart-card {
    height: 100%;
    padding: 15px;
    margin-bottom: 20px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.chart-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.chart-container {
    width: 100%;
    height: 350px;
    position: relative;
    overflow: hidden;
    background-color: #fff;
}

.echarts-container {
    width: 100% !important;
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
}

.no-data {
    margin: 50px 0;
    text-align: center;
}

@media (max-width: 1200px) {
    .el-col {
        &[class*="el-col-8"] {
            width: 50% !important;
        }
    }
}

@media (max-width: 768px) {
    .el-col {
        &[class*="el-col-8"] {
            width: 100% !important;
        }
    }
}
</style>